import { HitData, SearchFilters } from 'common/store/common/search/types';
import commonNavigationService, { AppSpecifiedNavigationListService, QuestionConfig } from 'common/services/commonNavigationService';
import { CONTENT_QTI_SECTION, CONTENT_STRUCTURE_ASSESSMENT } from 'common/store/common/constants';
import { tl } from 'common/services/i18nService';

import { isQuestionMode } from 'common/util/shared/sharedUtils';
import { CommonContextState } from 'common/store/context/types';
import allQTIContextItemsJson from '../../docs/definitions/tabs/allQTIContextItems.json';
import allSectionsJson from '../../docs/definitions/tabs/allSections.json';

import { ContextState } from '../store/context/reducer';
import urlContextParametersService from './urlContextParametersService';
import { QTI_CONTEXT_INDEX, RESOURCE } from '../store/common';
import { RootState } from '../store/rootReducer';
import storeManager from '../store';
import { setDirtyCurrentQtiContextItem, toggleNavigationLock } from '../store/context/actions';
import { AdditionalResourceTypes, navigableResourceType } from '../store/context/types';

// A read-only item (padlock shown) cannot have legitimate unsaved changes, so the
// "unsaved changes" confirmation should never be shown for it even if a stale
// navigationLock/edited flag is set.
const isCurrentContextReadOnly = (context: ContextState): boolean => {
    return !!(
        context?.currentQtiContextItem?.isTrashed
        || context?.isLocked
        || context?.contentEditStatus?.isReadOnlyContent
    );
};

const navigationListService: AppSpecifiedNavigationListService = {
    getDefaultTabsForDataType(dataType: string): any[] {
        switch (dataType) {
        case QTI_CONTEXT_INDEX:
            return [allQTIContextItemsJson];
        default:
            return [allSectionsJson];
        }
    },
    getContextValue(context: ContextState, contextKey: string): any {
        return context[contextKey];
    },
    getSearchFiltersForContext: (context: ContextState, contextFilters: string[]): SearchFilters => {
        const searchFilter: SearchFilters = {};
        contextFilters.forEach((contextKey: string) => {
            if (contextKey === 'contentItemIds') {
                searchFilter.objectId = navigationListService.getContextValue(context, 'contentItemIds');
            } else {
                searchFilter[contextKey] = navigationListService.getContextValue(context, contextKey);
            }
        });
        return searchFilter;
    },
    getEmptyHitResponse(context: ContextState): HitData[] {
        if (!context) {
            return [];
        }

        const { contextId, selectedSectionTitle } = context;
        return [{
            objectId: contextId,
            title: selectedSectionTitle,
        }];
    },
    getHitValuesFromNavigation(hit: HitData) {
        return {
            objectId: hit.objectId,
        };
    },

    navigateToWorkspace(workspaceId: string, silent?: boolean): void {
        const { context: rootContext } = storeManager.getState() as RootState;

        if (rootContext?.navigationLock) {
            if (!isCurrentContextReadOnly(rootContext) && !confirm(tl('common.navigationLock.confirmToContinue'))) {
                return;
            }
            storeManager.dispatch(toggleNavigationLock(false));
            storeManager.dispatch(setDirtyCurrentQtiContextItem(false));
        }
        commonNavigationService.setSetParamsInSearchString({ workspaceId }, { silent, isAuthorCall: true });
    },

    navigateTo(hit: HitData, dataType?: string): void {
        const { context } = storeManager.getState() as RootState;
        if (context?.navigationLock) {
            if (isCurrentContextReadOnly(context) || confirm(tl('common.navigationLock.confirmToContinue'))) {
                storeManager.dispatch(toggleNavigationLock(false));
                storeManager.dispatch(setDirtyCurrentQtiContextItem(false));
            } else {
                return;
            }
        }

        const isStimulus = hit.type === AdditionalResourceTypes.STIMULUS
            && hit.contentItemId
            && window.marketplaceProperties.ENABLE_STIMULUS_IN_QUESTION;

        const shouldSetQtiContextId = hit.qtiElementType || (isQuestionMode() && (hit.objectId || isStimulus));

        if (shouldSetQtiContextId) {
            const contextId = hit.objectId ?? hit.contentItemId;
            urlContextParametersService.setQtiContextItemId(contextId);
        } else if (hit.type && dataType === RESOURCE) {
            if (navigableResourceType.includes(hit.type)) {
                if (hit.type === AdditionalResourceTypes.RESOURCE_LINK && hit.link) {
                    urlContextParametersService.setResourceLink(hit.link);
                } else {
                    urlContextParametersService.setResourceContextItemId(hit.contentItemId);
                }
            } else {
                urlContextParametersService.resetQtiContextItemIdAndResourceContextItemId();
            }
        } else {
            if(isQuestionMode() && window.marketplaceProperties.ENABLE_STIMULUS_IN_QUESTION) {
                urlContextParametersService.setQtiContextItemId(hit.objectId);
            } else {
                urlContextParametersService.setContextId(hit.objectId);
            }
        }
    },

    navigateToCurrentQtiContextItem(): void {
        const { context } = storeManager.getState() as RootState;
        if (context.currentQtiContextItem) {
            navigationListService.navigateTo(context.currentQtiContextItem as HitData);
        }
    },

    redirectToLogin(): void {
        window.location.href = '/admin?alert=noSession';
    },

    openQuestionInEditMode(options: QuestionConfig): void {
        const {
            contentItemId,
            contentType = 'content_question',
            mode = 'question',
            useQuestionEditOnly = true,
            itemBankId,
            isTemplate = false,
        } = options;

        const isStructureEditorOnQuestionEditEnabled = window.marketplaceProperties.ENABLE_STRUCTURE_EDITOR_ON_QUESTION_EDIT;
        let baseUrl = useQuestionEditOnly ? '/author/author_question_edit_only/' : '/author/author_edit/';

        if (isStructureEditorOnQuestionEditEnabled) {
            baseUrl = '/author/author_question_edit_only_with_editor/';
        }

        const urlParams = new URLSearchParams({
            contentType,
            contentItemIds: String(contentItemId),
            contextId: String(contentItemId),
        });

        if (useQuestionEditOnly) {
            urlParams.append('mode', mode);
        }

        if (itemBankId && itemBankId !== -1) {
            urlParams.append('itemBankId', String(itemBankId));
        }

        if (isTemplate) {
            urlParams.append('contentTemplate', 'content_item_templates');
            urlParams.append('isTemplate', isTemplate.toString());
        }

        window.location.href = `${baseUrl}?${urlParams.toString()}`;
    },
    backToOriginalLists(context: ContextState): void {
        const {
            itemBankId,
            currentItemContext: {
                contentType,
                isTemplate,
            },
            isIBTemplate,
        } = context;

        let url = '/admin#itemBank';
        if (context && itemBankId > -1) {
            url += `/${itemBankId}`;
        }
        if (contentType === CONTENT_QTI_SECTION) {
            url += isTemplate ? '/sectionTemplates' : '/sections';
        } else if (isIBTemplate) {
            url += '/contentItemTemplates';
        } else if (isQuestionMode() || contentType.startsWith('content_question')) {
            url += isIBTemplate ? '/contentItemTemplates' : '/questions';
        } else if (contentType === CONTENT_STRUCTURE_ASSESSMENT) {
            url += isTemplate ? '/questionSetTemplates' : '';
        }

        window.location.href = url;
    },
    validateContext: (context: CommonContextState) => {
        return context.currentQtiContextItem === undefined;
    },
};

export default navigationListService;
